/**
 * Script Error Diagnostics Plugin
 *
 * Enhances error reporting for script loading failures, including:
 * - SyntaxError: Unexpected EOF (corrupted/truncated scripts)
 * - Script load failures (404s, network errors)
 *
 * When a SyntaxError occurs, this plugin captures a snapshot of all scripts
 * in the DOM to help identify which script caused the failure.
 */
import { lazyAddBreadcrumb, lazySetContext } from '~/utility/lazySentry'

interface ScriptLoadEvent {
  src: string
  timestamp: number
  success: boolean
}

export default defineNuxtPlugin(() => {
  // Track script loading history (ring buffer, last 50 scripts)
  const scriptLoadHistory: ScriptLoadEvent[] = []
  const MAX_HISTORY = 50
  // Capture all scripts currently in the DOM
  function getScriptAudit() {
    const scripts = Array.from(document.querySelectorAll('script'))
    return scripts.map((script, index) => {
      const isInline = !script.src
      const content = script.textContent || ''

      return {
        index,
        src: script.src || '(inline)',
        type: script.type || 'text/javascript',
        async: script.async,
        defer: script.defer,
        // For inline scripts, capture length and preview
        inlineLength: isInline ? content.length : undefined,
        inlinePreview: isInline ? content.slice(0, 150) : undefined,
        inlineTail:
          isInline && content.length > 50 ? content.slice(-50) : undefined,
        id: script.id || undefined,
        parentElement: script.parentElement?.tagName,
      }
    })
  }

  // Listen for script load/error events globally (capture phase)
  document.addEventListener(
    'load',
    (event) => {
      const target = event.target as HTMLElement
      if (target?.tagName === 'SCRIPT') {
        const script = target as HTMLScriptElement
        if (script.src) {
          scriptLoadHistory.push({
            src: script.src,
            timestamp: Date.now(),
            success: true,
          })

          // Keep history bounded
          if (scriptLoadHistory.length > MAX_HISTORY) {
            scriptLoadHistory.shift()
          }
        }
      }
    },
    true,
  )

  document.addEventListener(
    'error',
    (event) => {
      const target = event.target as HTMLElement
      if (target?.tagName === 'SCRIPT') {
        const script = target as HTMLScriptElement
        const src = script.src || '(inline)'

        scriptLoadHistory.push({
          src,
          timestamp: Date.now(),
          success: false,
        })

        // Keep history bounded
        if (scriptLoadHistory.length > MAX_HISTORY) {
          scriptLoadHistory.shift()
        }

        // Add breadcrumb for script load failure
        lazyAddBreadcrumb({
          type: 'error',
          category: 'script.load.failed',
          message: `Script failed to load: ${src}`,
          level: 'error',
          data: {
            src,
            type: script.type,
            async: script.async,
            defer: script.defer,
          },
        })

        console.error('[Script Diagnostics] Script failed to load:', src)
      }
    },
    true,
  )

  // Listen for SyntaxErrors and enhance with script context
  window.addEventListener(
    'error',
    (event) => {
      const error = event.error as Error | undefined

      // Check if this is an EOF/truncation SyntaxError specifically
      const isEOFError =
        error instanceof SyntaxError ||
        event.message?.includes('SyntaxError') ||
        event.message?.includes('Unexpected EOF') ||
        event.message?.includes('Unexpected end of input') ||
        event.message?.includes('Unexpected end of script')

      if (!isEOFError) return

      const scriptAudit = getScriptAudit()
      const inlineScripts = scriptAudit.filter((s) => s.src === '(inline)')
      const externalScripts = scriptAudit.filter((s) => s.src !== '(inline)')

      // Add comprehensive context to Sentry
      lazySetContext('script_diagnostics', {
        totalScripts: scriptAudit.length,
        inlineScripts: inlineScripts.length,
        externalScripts: externalScripts.length,
        errorFilename: event.filename,
        errorLineno: event.lineno,
        errorColno: event.colno,
        recentLoadHistory: scriptLoadHistory.slice(-10),
      })

      // Add all inline scripts for inspection (they're often the culprit)
      if (inlineScripts.length > 0) {
        lazySetContext(
          'inline_scripts',
          Object.fromEntries(
            inlineScripts.slice(0, 20).map((s, i) => [
              `script_${i}`,
              {
                length: s.inlineLength,
                preview: s.inlinePreview,
                tail: s.inlineTail,
                parent: s.parentElement,
              },
            ]),
          ),
        )
      }

      // Add external scripts list
      if (externalScripts.length > 0) {
        lazySetContext(
          'external_scripts',
          Object.fromEntries(
            externalScripts.slice(0, 30).map((s, i) => [
              `script_${i}`,
              {
                src: s.src,
                type: s.type,
                async: s.async,
                defer: s.defer,
              },
            ]),
          ),
        )
      }

      // Add breadcrumb with summary
      lazyAddBreadcrumb({
        type: 'error',
        category: 'script.syntax_error',
        message: `SyntaxError detected - captured ${scriptAudit.length} scripts for analysis`,
        level: 'error',
        data: {
          errorMessage: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      })

      console.error('[Script Diagnostics] SyntaxError detected:', {
        message: event.message,
        filename: event.filename,
        line: event.lineno,
        col: event.colno,
        totalScripts: scriptAudit.length,
      })
    },
    true,
  )

  // Expose audit function for debugging in development only
  if (import.meta.dev) {
    const diagnostics = {
      getScriptAudit,
      getLoadHistory: () => [...scriptLoadHistory],
    }
    ;(
      window as Window & { __scriptDiagnostics?: typeof diagnostics }
    ).__scriptDiagnostics = diagnostics
  }
})
